import { useState } from 'react'
import toast from 'react-hot-toast'
import type { ReactNode } from 'react'
import {
  MARKET_PRICES,
  estimateMissionRhum,
  tradeCargoCap,
  estimateBuyCost,
  crewWageCut,
  netAfterCrewWage,
  useTradeEstimate,
  useLaunchTradeRun,
  useLaunchBuy,
  useMyFleet,
  useMyMissions,
  ongoingMissionTo,
  type MapIslandDto,
} from '@/api/hooks'
import { GoldButton, HintBubble, ResourceTally } from '@/components/game/ui'
import { GoldIcon, WoodIcon, RumIcon } from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import {
  RationsLabel,
  CargoInput,
  AmbushThreatCard,
  ReputBreakdown,
  ShipPickerList,
} from '@/components/game/map/launcherBits'
import { cargoMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { CARGO_SORTS } from '@/components/game/map/shipPicker/useShipFilter'
import { missionFleetCap } from '@/lib/fleetGroups'
import { tripDurationSeconds, TripDuration, convoySpeedPerHour, ConvoyPace } from '@/components/game/map/tripDuration'

export function TradeRunLauncher({ destination }: { destination: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchTradeRun()
  // One commerce run at a time per port — covers sell AND buy (same type, 409).
  const ongoing = ongoingMissionTo(missionsData?.missions ?? [], destination.id, 'commerce')
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const origin = fleet?.island
  const resources = fleet?.resources

  const fleetCap = missionFleetCap(fleet?.shipyard?.level)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [woodInput, setWoodInput] = useState(0)
  const [rumInput, setRumInput] = useState(0)

  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  // A specialty island won't buy back its own good: a scierie (specialty wood)
  // only loads rhum, a distillerie (rhum) only wood. Everywhere else (no
  // specialty) takes both. We clamp the forbidden good to 0 so a stale value can
  // never be estimated or shipped, and hide its input below.
  const sellWood = destination.specialty !== 'wood'
  const sellRhum = destination.specialty !== 'rhum'
  const wood = sellWood ? woodInput : 0
  const rum = sellRhum ? rumInput : 0

  // Commerce cap = the fleet's pooled effective hold (base × hold module) × the
  // account's Forge cargo multiplier — the exact volume the server accepts at launch.
  const forgeMult = fleet?.cargoForgeMult ?? 1
  const capacity = tradeCargoCap(selected.reduce((acc, s) => acc + s.cargoCapacity, 0), forgeMult)
  const totalCargo = wood + rum
  const distance = origin ? Math.sqrt((destination.x - origin.x) ** 2 + (destination.y - origin.y) ** 2) : 0
  // Header affordance only — the destination's base price band (×1.3 comptoir…).
  const multiplier = MARKET_PRICES.destinationMultiplier[destination.type] ?? 1

  // Server-side sale breakdown (base · réput +Y · total) at the player's current
  // reputation — the front no longer mirrors balance constants for the estimate.
  const estimate = useTradeEstimate(selectedIds, destination.id, wood, rum, selected.length > 0)
  const estimatedGold = estimate.data?.total ?? 0

  const overflow = totalCargo > capacity
  const tooMuchWood = resources ? wood > resources.wood : false
  const tooMuchRum = resources ? rum > resources.rum : false
  const slowestSpeed = convoySpeedPerHour(selected, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (distance * 2) / slowestSpeed : 0
  const tripSeconds = origin && selected.length > 0 ? tripDurationSeconds(slowestSpeed, origin, destination) : 0
  const rhumEstimate = estimateMissionRhum(selected, Math.max(60, roundTripHours * 3600))
  const notEnoughRhum = resources ? rhumEstimate + rum > resources.rum : false

  const canLaunch =
    selected.length > 0 &&
    resources !== undefined && resources !== null &&
    totalCargo > 0 &&
    !overflow &&
    !tooMuchWood &&
    !tooMuchRum &&
    !notEnoughRhum &&
    ongoing == null &&
    !launch.isPending

  if (dockedShips.length === 0) {
    return (
      <div className="bg-sand-300/10 rounded-lg p-3 space-y-2 border border-sand-300/30">
        <p className="text-xs text-sand-300 italic">
          Aucun navire à quai pour cette expédition.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-sand-300/5 rounded-lg p-3 space-y-2 border border-sand-300/30">
      <p className="text-xs uppercase tracking-wider flex items-center gap-1.5">
        <GoldIcon className="w-4 h-4 text-sand-300" /><span className="text-sand-300">Expédition commerciale</span>
        <span className="ml-1 text-ocean-300">×{multiplier.toFixed(1)}</span>
      </p>
      <div className="flex items-center justify-between text-[11px] text-ocean-300">
        <span>Navires</span>
        <span>Cap flotte : <strong className="text-sand-300">{fleetCap}</strong></span>
      </div>
      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={cargoMeta}
        sorts={CARGO_SORTS}
        fleetWord="flotte"
      />

      {/* Cargo shortcuts — fill the hold without typing. A specialty island only
          loads the good it accepts, so its irrelevant shortcuts drop out. */}
      <div className="flex flex-wrap gap-1">
        {[
          sellWood && {
            label: 'Max bois',
            apply: () => { setWoodInput(Math.min(resources?.wood ?? 0, capacity)); setRumInput(0) },
          },
          sellRhum && {
            label: 'Max rhum',
            apply: () => { setRumInput(Math.min(resources?.rum ?? 0, capacity)); setWoodInput(0) },
          },
          sellWood && sellRhum && {
            label: '50/50',
            apply: () => {
              const half = Math.floor(capacity / 2)
              const w = Math.min(half, resources?.wood ?? 0)
              setWoodInput(w)
              setRumInput(Math.min(capacity - w, resources?.rum ?? 0))
            },
          },
          { label: 'Vider', apply: () => { setWoodInput(0); setRumInput(0) } },
        ].filter((s): s is { label: string; apply: () => void } => Boolean(s)).map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={s.apply}
            disabled={capacity <= 0}
            className="text-[11px] px-2 py-1 rounded bg-ocean-900/70 border border-sand-500/20 hover:border-sand-300/40 transition-colors disabled:opacity-40"
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className={`grid gap-2 ${sellWood && sellRhum ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {sellWood && (
          <CargoInput
            label="Bois"
            Icon={WoodIcon}
            value={woodInput}
            max={Math.min(resources?.wood ?? 0, capacity)}
            error={tooMuchWood}
            onChange={setWoodInput}
          />
        )}
        {sellRhum && (
          <CargoInput
            label="Rhum"
            Icon={RumIcon}
            value={rumInput}
            max={Math.min(resources?.rum ?? 0, capacity)}
            error={tooMuchRum}
            onChange={setRumInput}
          />
        )}
      </div>

      <p className="text-xs text-ocean-200 flex items-center justify-between flex-wrap gap-1">
        <span className={overflow ? 'text-red-400' : ''}>Cale {totalCargo}/{capacity}</span>
        {selected.length > 0 && <TripDuration seconds={tripSeconds} />}
        <span className="text-sand-300 inline-flex items-center gap-1">net ≈ <ResourceTally gold={netAfterCrewWage(estimatedGold)} /></span>
      </p>
      <ConvoyPace ships={selected} tripSeconds={tripSeconds} />
      {estimate.data !== undefined && !estimate.isFetching && totalCargo > 0 && (
        <ReputBreakdown
          base={estimate.data.base}
          reputationBonus={estimate.data.reputationBonus}
          total={estimate.data.total}
        />
      )}
      {estimate.data !== undefined && !estimate.isFetching && totalCargo > 0 && (estimate.data.ambushRisk ?? 0) > 0 && (
        <AmbushThreatCard
          risk={estimate.data.ambushRisk ?? 0}
          threat={estimate.data.ambushThreat}
          fleet={estimate.data.ambushFleet}
        />
      )}
      <p className="text-[11px] text-ocean-300 inline-flex items-center gap-1 flex-wrap">
        <span className="text-ocean-400 inline-flex items-center gap-1">part équipage −<ResourceTally gold={crewWageCut(estimatedGold)} /></span>
      </p>
      <p className="text-xs text-ocean-300 flex items-center gap-1 flex-wrap">
        <RationsLabel /> : <span className={`inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" /></span>
        {notEnoughRhum && <span className="text-red-400">(rhum insuffisant en banque)</span>}
      </p>

      {ongoing && (
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <GoldIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>
            Une expédition marchande est déjà en cours vers cette île
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
      )}

      <GoldButton
        onClick={() => {
          if (selected.length === 0) return
          launch.mutate(
            { shipIds: selected.map((s) => s.id), destinationIslandId: destination.id, wood, rum },
            {
              onSuccess: () => {
                toast.success(`${selected.length} navire(s) → ${destination.name}.`)
                setWoodInput(0)
                setRumInput(0)
                setSelectedIds([])
              },
              onError: (err) =>
                toast.error(err.message || 'Expédition impossible'),
            },
          )
        }}
        disabled={!canLaunch}
        className="w-full text-sm px-3 py-1.5"
      >
        {ongoing
          ? 'Expédition en cours'
          : launch.isPending
            ? 'Lancement…'
            : selected.length === 0
              ? 'Choisis une flotte'
              : overflow
                ? 'Cale insuffisante'
                : totalCargo === 0
                  ? 'Charge la cale'
                  : `Lever l'ancre`}
      </GoldButton>
    </div>
  )
}

/**
 * Trade panel at a destination. Buyable ports (buyOptions non-empty — comptoir,
 * scierie, distillerie) also let you BUY goods with gold, so they get a
 * Vendre/Acheter toggle; everywhere else it's sell-only.
 */
export function TradeSection({ destination }: { destination: MapIslandDto }) {
  const canBuy = destination.buyOptions.length > 0
  const [mode, setMode] = useState<'sell' | 'buy'>('sell')
  const cell = (active: boolean) =>
    `flex-1 px-2 py-1 rounded-md transition-colors ${active ? 'bg-sand-300/15 text-sand-200 font-semibold' : 'text-ocean-300 hover:text-sand-300'}`

  return (
    <div className="space-y-2">
      {canBuy && (
        <div className="flex gap-1 rounded-lg border border-sand-500/20 bg-ocean-900/50 p-1 text-xs" role="group" aria-label="Sens du commerce">
          <button type="button" onClick={() => setMode('sell')} aria-pressed={mode === 'sell'} className={cell(mode === 'sell')}>Vendre</button>
          <button type="button" onClick={() => setMode('buy')} aria-pressed={mode === 'buy'} className={cell(mode === 'buy')}>Acheter</button>
        </div>
      )}
      {canBuy && mode === 'buy'
        ? <BuyGoodsLauncher destination={destination} />
        : <TradeRunLauncher destination={destination} />}
    </div>
  )
}

const BUY_RESOURCE_META: Record<'wood' | 'rhum', { Icon: (p: { className?: string }) => ReactNode; noun: string }> = {
  wood: { Icon: WoodIcon, noun: 'du bois' },
  rhum: { Icon: RumIcon, noun: 'du rhum' },
}

/**
 * Buy a resource with gold at a buyable port. Which goods are on offer comes from
 * `destination.buyOptions` (comptoir → bois ET rhum, scierie → bois, distillerie
 * → rhum). The ship sails the round trip and returns laden; volume is bounded by
 * the hold AND free warehouse space (per-resource), the gold cost (port base ×
 * distance) is debited at launch. Buying always costs more per unit than selling
 * — pure gold sink, no arbitrage.
 */
export function BuyGoodsLauncher({ destination }: { destination: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchBuy()
  // Commerce lock (shared with the sell run — same mission type toward the port).
  const ongoing = ongoingMissionTo(missionsData?.missions ?? [], destination.id, 'commerce')
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const origin = fleet?.island
  const resources = fleet?.resources

  const options = destination.buyOptions
  // Selected good. Defaults to the first option; only meaningful when >1 (comptoir).
  const [resource, setResource] = useState<'wood' | 'rhum'>(options[0]?.resource ?? 'wood')
  const [amount, setAmount] = useState(0)

  // Keep the selection valid against the port's offer (and reset qty on switch).
  const selected = options.find((o) => o.resource === resource) ?? options[0] ?? null
  const { Icon, noun } = BUY_RESOURCE_META[selected?.resource ?? 'wood']

  const fleetCap = missionFleetCap(fleet?.shipyard?.level)
  const [selectedShipIds, setSelectedShipIds] = useState<number[]>([])
  const selectedShips = selectedShipIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
  const toggleShip = (id: number) => {
    setSelectedShipIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedShipIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  // Same commerce cap as a sell run: effective hold × the Forge cargo multiplier.
  const forgeMult = fleet?.cargoForgeMult ?? 1
  const cargoCap = tradeCargoCap(selectedShips.reduce((acc, s) => acc + s.cargoCapacity, 0), forgeMult)
  // Bounded by the HOLD only — the warehouse may free up before the ship returns.
  // Surplus that doesn't fit on arrival is lost at sea (we just warn, never block).
  const maxBuy = cargoCap
  // Free warehouse space is PER-RESOURCE (the cap is per-resource).
  const stored = resources ? (selected?.resource === 'rhum' ? resources.rum : resources.wood) : 0
  const freeStorage = resources ? Math.max(0, resources.capacity - stored) : 0
  const distance = origin ? Math.sqrt((destination.x - origin.x) ** 2 + (destination.y - origin.y) ** 2) : 0
  const basePrice = selected?.basePrice ?? 0
  // Delivered price per unit = port base × distance premium — same premium the
  // sell side pays. Shown in the header so qty × unit ≈ total.
  const effectiveUnit = Math.round(basePrice * (1 + MARKET_PRICES.distancePremiumPerLeague * distance))
  const goldCost = estimateBuyCost(amount, basePrice, distance)

  const slowestSpeed = convoySpeedPerHour(selectedShips, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (distance * 2) / slowestSpeed : 0
  const tripSeconds = origin && selectedShips.length > 0 ? tripDurationSeconds(slowestSpeed, origin, destination) : 0
  const rhumEstimate = estimateMissionRhum(selectedShips, Math.max(60, roundTripHours * 3600))

  const overCap = amount > maxBuy
  const overStorage = resources ? amount > freeStorage : false
  const notEnoughGold = resources ? goldCost > resources.gold : false
  const notEnoughRhum = resources ? rhumEstimate > resources.rum : false
  const canLaunch =
    selectedShips.length > 0 && selected !== null && resources !== undefined && resources !== null
    && amount > 0 && !overCap && !notEnoughGold && !notEnoughRhum && ongoing == null && !launch.isPending

  const selectResource = (next: 'wood' | 'rhum') => {
    setResource(next)
    setAmount(0)
  }
  const tab = (active: boolean) =>
    `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md transition-colors ${active ? 'bg-sand-300/15 text-sand-200 font-semibold' : 'text-ocean-300 hover:text-sand-300'}`

  if (dockedShips.length === 0) {
    return (
      <div className="bg-sand-300/10 rounded-lg p-3 border border-sand-300/30">
        <p className="text-xs text-sand-300 italic flex items-center gap-1">
          Aucun navire à quai pour aller chercher {noun.split(' ')[0]} <Icon className="w-3.5 h-3.5 text-sand-300" />.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-sand-300/5 rounded-lg p-3 space-y-2 border border-sand-300/30">
      {options.length > 1 && (
        <div className="flex gap-1 rounded-lg border border-sand-500/20 bg-ocean-900/50 p-1 text-xs" role="group" aria-label="Ressource à acheter">
          {options.map((o) => {
            const m = BUY_RESOURCE_META[o.resource]
            return (
              <button
                key={o.resource}
                type="button"
                onClick={() => selectResource(o.resource)}
                aria-pressed={selected?.resource === o.resource}
                className={tab(selected?.resource === o.resource)}
              >
                <m.Icon className="w-3.5 h-3.5" /> {o.resource === 'wood' ? 'Bois' : 'Rhum'}
              </button>
            )
          })}
        </div>
      )}

      <p className="text-xs uppercase tracking-wider flex items-center gap-1.5 flex-wrap">
        <Icon className="w-4 h-4 text-sand-300" /><span className="text-sand-300">Acheter {noun}</span>
        <span className="group/tip relative ml-1 inline-flex items-center gap-1 text-ocean-300 cursor-help underline decoration-dotted decoration-ocean-500/50 underline-offset-2">
          ≈ <ResourceTally gold={effectiveUnit} /><span aria-hidden="true">/</span><Icon className="w-3 h-3" />
          <HintBubble
            className="w-60"
            label={
              <>
                <span className="font-semibold text-sand-200">Prix livré</span> : base{' '}
                <span className="inline-flex items-center gap-0.5 text-sand-200">{basePrice} <GoldIcon className="w-3 h-3" />/<Icon className="w-3 h-3" /></span> (propre à ce port) × prime de
                distance, comme la vente. Un port proche ou « bon marché » revient moins cher.
              </>
            }
          />
        </span>
      </p>
      <div className="flex items-center justify-between text-[11px] text-ocean-300">
        <span>Navires</span>
        <span>Cap flotte : <strong className="text-sand-300">{fleetCap}</strong></span>
      </div>
      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedShipIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedShipIds([])}
        meta={cargoMeta}
        sorts={CARGO_SORTS}
        fleetWord="flotte"
      />

      <div className="flex flex-wrap gap-1">
        <button type="button" onClick={() => setAmount(maxBuy)} disabled={maxBuy <= 0}
          className="text-[11px] px-2 py-1 rounded bg-ocean-900/70 border border-sand-500/20 hover:border-sand-300/40 transition-colors disabled:opacity-40">Max ({maxBuy})</button>
        <button type="button" onClick={() => setAmount(0)}
          className="text-[11px] px-2 py-1 rounded bg-ocean-900/70 border border-sand-500/20 hover:border-sand-300/40 transition-colors">Vider</button>
      </div>

      <CargoInput label="à acheter" Icon={Icon} value={amount} max={maxBuy} error={overCap} onChange={setAmount} />

      <p className="text-xs text-ocean-200 flex items-center justify-between flex-wrap gap-1">
        <span className="inline-flex items-center gap-1">Coût ≈ <ResourceTally gold={goldCost} className={notEnoughGold ? 'text-red-400 font-semibold' : 'text-sand-300'} /></span>
        {selectedShips.length > 0 && <TripDuration seconds={tripSeconds} />}
      </p>
      <ConvoyPace ships={selectedShips} tripSeconds={tripSeconds} />
      <p className="text-xs text-ocean-300 flex items-center gap-1 flex-wrap">
        <RationsLabel /> : <span className={`inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" /></span>
      </p>
      {overStorage && amount > 0 && (
        <p className="text-[11px] text-rum-300 leading-snug">
          ⚠️ ~{freeStorage} de place en entrepôt. Au-delà, le surplus sera <strong>perdu en mer</strong> s'il ne rentre pas au retour.
        </p>
      )}

      {ongoing && (
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <GoldIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>
            Une expédition marchande est déjà en cours vers cette île
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
      )}

      <GoldButton
        onClick={() => {
          if (selectedShips.length === 0) return
          if (!selected) return
          launch.mutate(
            { shipIds: selectedShips.map((s) => s.id), destinationIslandId: destination.id, resource: selected?.resource, wood: amount },
            {
              onSuccess: () => {
                toast.success(`${selectedShips.length} navire(s) → ${BUY_RESOURCE_META[selected.resource].noun} à ${destination.name}.`)
                setAmount(0)
                setSelectedShipIds([])
              },
              onError: (err) => toast.error(err.message || 'Achat impossible'),
            },
          )
        }}
        disabled={!canLaunch}
        className="w-full text-sm px-3 py-1.5"
      >
        {ongoing
          ? 'Expédition en cours'
          : launch.isPending
            ? 'Lancement…'
            : selectedShips.length === 0
              ? 'Choisis une flotte'
              : amount <= 0
                ? 'Choisis une quantité'
                : notEnoughGold
                  ? 'Or insuffisant'
                  : overCap
                    ? 'Cale insuffisante'
                    : `Acheter (≈ ${goldCost} or)`}
      </GoldButton>
    </div>
  )
}
